/**
 * ThumbnailCache — the shared storage/freshness/notify primitive behind the two
 * viewport thumbnail systems (keyframe-thumbnails.ts and state-thumbnails.ts).
 *
 * Both systems cache `{ dataUrl, hash }` per (owner, name) card, skip captures
 * when the stored hash still matches the card's current content, and notify
 * subscribers when an image lands. That bookkeeping is identical; what DIFFERS is
 * each system's keying, its content-hash logic, and its capture DRIVER (the
 * keyframe abort-loop vs the state serialized queue). So this primitive owns only
 * the cache + freshness + listeners + FIFO eviction; the systems supply the rest.
 *
 * Keys are opaque strings the caller builds (it knows how to identify a card);
 * the cache never parses them.
 */

export interface ThumbnailEntry {
    dataUrl: string;
    /** Content hash of what this thumbnail depicts, to skip redundant captures. */
    hash: string;
}

/** Read a Blob/File as a data URL. Shared by both capture drivers. */
export function fileToDataUrl(file: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });
}

export class ThumbnailCache {
    private readonly entries = new Map<string, ThumbnailEntry>();
    private readonly listeners = new Set<() => void>();

    /**
     * @param maxEntries FIFO cap. Cache keys embed transform uniqueIds, which are
     * new on every scene load — without eviction, dataURLs (~20-100KB each) from
     * previous scenes stay pinned for the tab's lifetime. 0/undefined = unbounded.
     */
    constructor(private readonly maxEntries = 0) {}

    get(key: string): ThumbnailEntry | undefined {
        return this.entries.get(key);
    }

    /** The cached data URL for a card, if any. */
    getDataUrl(key: string): string | undefined {
        return this.entries.get(key)?.dataUrl;
    }

    /** True when the stored entry's hash matches the card's current content hash. */
    isFresh(key: string, currentHash: string): boolean {
        const entry = this.entries.get(key);
        return entry !== undefined && entry.hash === currentHash;
    }

    /** Store an entry (FIFO-evicting the oldest when at capacity) and notify. */
    set(key: string, entry: ThumbnailEntry): void {
        if (this.maxEntries > 0 && !this.entries.has(key) && this.entries.size >= this.maxEntries) {
            // Map iterates in insertion order — drop the oldest entry.
            const oldest = this.entries.keys().next().value;
            if (oldest !== undefined) this.entries.delete(oldest);
        }
        this.entries.set(key, entry);
        this.notify();
    }

    /** Drop a card so it shows a placeholder until recaptured. Notifies if present. */
    delete(key: string): void {
        if (this.entries.delete(key)) this.notify();
    }

    /** Move an entry to a new key (rename). No-op if the source is absent. */
    rename(oldKey: string, newKey: string): void {
        const entry = this.entries.get(oldKey);
        if (entry === undefined) return;
        this.entries.delete(oldKey);
        this.set(newKey, entry);
    }

    /**
     * Copy an entry to a new key (duplicate) with a CLEARED hash, so the next
     * freshness check re-captures the copy from its own content rather than
     * trusting the source's hash. No-op if the source is absent.
     */
    copyWithStaleHash(sourceKey: string, destKey: string): void {
        const entry = this.entries.get(sourceKey);
        if (entry === undefined) return;
        this.set(destKey, { dataUrl: entry.dataUrl, hash: '' });
    }

    /** Subscribe to cache changes. Returns an unsubscribe function. */
    subscribe(callback: () => void): () => void {
        this.listeners.add(callback);
        return () => this.listeners.delete(callback);
    }

    notify(): void {
        for (const cb of this.listeners) cb();
    }
}
